import { useEffect, useState } from 'react';
import { useV2 } from './ctx';
import { Sheet } from './ui';
import { jt, jtS, shortDate } from './model';

// Analisis › Armada (Director, 18–19 Oct). Server: services/armada.js.
// 19 Oct: "apple level UI UX … i dont like how to switch between trucks and
// non trucks. the trucks view ritase is also messy. and i want a way to
// identify the repairs per truck." So: one card per truck (what it left,
// its trips as one bar, its repairs), tap a card for the truck's account
// (every trip and every cost booked to it). Repairs come from Accurate's
// Departemen on purchase invoices, which accounting sets to the truck.
const dec = (x, n = 2) => (x == null ? '—' : x.toLocaleString('id-ID', { minimumFractionDigits: n, maximumFractionDigits: n }));
const monthName = (mk, opt = 'long') => new Date(mk + '-01T00:00:00').toLocaleDateString('id-ID', { month: opt, year: opt === 'long' ? 'numeric' : undefined });
const kon = m => (m.pendapatan || 0) - (m.uj || 0) - (m.koordinasi || 0) - (m.perbaikan || 0) - (m.leasing || 0);

// "▲ 2,1 jt" against last month; count = trips, not rupiah
function Delta({ cur, prev, good = 'up', count, inline }) {
  if (prev == null) return null;
  const d = cur - prev;
  const cls = inline ? 'dl' : 'dl blk';
  if (count ? d === 0 : Math.abs(d) < 0.05e6) return <small className={`${cls} muted`}>±0</small>;
  const ok = good === 'up' ? d > 0 : d < 0;
  return <small className={`${cls} ${ok ? 'pos' : 'bad'}`}>{d > 0 ? '▲' : '▼'} {count ? Math.abs(d) : jt(Math.abs(d))}</small>;
}

// Trips as one bar: dark = pergi (Serang → Lampung), light = balikan.
// Width is relative to the busiest truck, so the cards compare at a glance.
function TripBar({ m, max }) {
  const w = n => `${max ? (n / max) * 100 : 0}%`;
  return (
    <div className="ar-trip">
      <div className="ar-bar" aria-hidden="true"><span className="go" style={{ width: w(m.pergi || 0) }} /><span className="back" style={{ width: w(m.balikan || 0) }} /></div>
      <span className="ar-rit"><b className="num">{m.rit || 0}</b> rit <span className="muted">· {m.pergi || 0} pergi, {m.balikan || 0} balik</span></span>
    </div>
  );
}

export default function Armada({ mk }) {
  const { api } = useV2();
  const [d, setD] = useState(null);
  const [truck, setTruck] = useState(null);
  useEffect(() => { let live = true; const c = new AbortController(); setD(null); api.armada(mk, c.signal).then(r => live && setD(r)).catch(e => live && setD({ error: e.message })); return () => { live = false; c.abort(); }; }, [api, mk]);
  if (!d) return <p className="hint">Memuat…</p>;
  if (d.error) return <p className="hint bad">{d.error}</p>;
  if (!d.months.includes(mk)) return <p className="hint">Data armada mulai Agustus 2026.</p>;
  const prevK = d.months[d.months.indexOf(mk) - 1] || null;
  return (
    <>
      <PerTruk d={d} mk={mk} prevK={prevK} onOpen={setTruck} />
      <PerRute d={d} mk={mk} prevK={prevK} />
      {truck && <TruckSheet no={truck} mk={mk} onClose={() => setTruck(null)} />}
    </>
  );
}

function PerTruk({ d, mk, prevK, onOpen }) {
  const rows = d.trucks.map(t => ({ no: t.no, m: t.months[mk] || {}, p: prevK ? t.months[prevK] || {} : null })).sort((a, b) => kon(b.m) - kon(a.m));
  const sum = (k, side = 'm') => rows.reduce((t, r) => t + ((r[side] || {})[k] || 0), 0);
  const sh = d.shared[mk] || {}, shP = prevK ? d.shared[prevK] || {} : null;
  const shTot = s => (s.uj || 0) + (s.koordinasi || 0) + (s.perbaikan || 0) + (s.leasing || 0);
  const totKon = rows.reduce((t, r) => t + kon(r.m), 0) - shTot(sh);
  const totKonP = prevK ? rows.reduce((t, r) => t + kon(r.p || {}), 0) - shTot(shP) : null;
  const maxRit = Math.max(...rows.map(r => r.m.rit || 0), 1);
  const rit = sum('rit'), ritP = prevK ? sum('rit', 'p') : null;
  const rep = sum('perbaikan') + (sh.perbaikan || 0), repP = prevK ? sum('perbaikan', 'p') + (shP.perbaikan || 0) : null;
  const topRep = rows.slice().sort((a, b) => (b.m.perbaikan || 0) - (a.m.perbaikan || 0))[0];
  const prevName = prevK ? monthName(prevK, 'short') : '';
  return (
    <>
      <div className="tri">
        <div className="hero"><span className="k">Rit armada</span><span className="v num">{rit}</span><span className="s">{sum('pergi')} pergi · {sum('balikan')} balik{prevK && <> · <Delta cur={rit} prev={ritP} count inline /> vs {prevName}</>}</span></div>
        <div className="hero"><span className="k">Sisa armada</span><span className={`v num ${totKon < 0 ? 'bad' : ''}`}>{jtS(totKon)} jt</span><span className="s">setelah UJ, koordinasi, perbaikan, leasing{prevK && <> · <Delta cur={totKon} prev={totKonP} inline /></>}</span></div>
        <div className="hero"><span className="k">Perbaikan</span><span className="v num">{jt(rep)} jt</span><span className="s">{topRep?.m.perbaikan ? `terbesar ${topRep.no} · ${jt(topRep.m.perbaikan)} jt` : 'belum ada'}{prevK && <> · <Delta cur={rep} prev={repP} good="down" inline /></>}</span></div>
      </div>

      <section className="sec">
        <div className="sec-h"><div><h3>Per truk</h3><p>urut dari sisa terbesar · ketuk truk untuk akunnya</p></div><span>juta</span></div>
        <div className="ar-grid">
          {rows.map(({ no, m, p }) => {
            const k = kon(m);
            return (
              <button key={no} type="button" className="ar-card" onClick={() => onOpen(no)}>
                <div className="ar-top"><span className="ar-no">{no}</span>{m.bengkel >= 1 && <span className={`ar-chip ${m.bengkel >= 7 ? 'bad' : ''}`}>{dec(m.bengkel, 0)} hr bengkel</span>}</div>
                <div className="ar-kon"><span className={`num ${k < 0 ? 'bad' : ''}`}>{jtS(k)}</span><small>jt sisa</small><Delta cur={k} prev={p ? kon(p) : null} inline /></div>
                <TripBar m={m} max={maxRit} />
                <dl className="ar-kv">
                  <div><dt>Pendapatan</dt><dd className="num">{jt(m.pendapatan || 0)}</dd></div>
                  <div><dt>UJ + koord.</dt><dd className="num">{jt((m.uj || 0) + (m.koordinasi || 0))}</dd></div>
                  <div><dt>Perbaikan</dt><dd className={`num ${(m.perbaikan || 0) >= 15e6 ? 'bad' : ''}`}>{jt(m.perbaikan || 0)}</dd></div>
                  <div><dt>Leasing</dt><dd className="num">{jt(m.leasing || 0)}</dd></div>
                </dl>
                <span className="ar-go">Akun truk ›</span>
              </button>
            );
          })}
        </div>
        {shTot(sh) >= 1e5 && <p className="hint">Tanpa truk (tidak masuk kartu di atas): {[sh.uj && `UJ ${jt(sh.uj)}`, sh.koordinasi && `koordinasi ${jt(sh.koordinasi)}`, sh.perbaikan && `bengkel umum ${jt(sh.perbaikan)}`, sh.leasing && `leasing ${jt(sh.leasing)}`].filter(Boolean).join(' · ')} jt. Sisa armada di atas sudah dikurangi ini.</p>}
        <p className="hint">Sisa = pendapatan − UJ − koordinasi − perbaikan − leasing, sebelum gaji, sewa dan kantor. Pendapatan dari faktur Accurate (satu baris per rit). Perbaikan dari faktur pembelian Accurate per Departemen (truk). UJ, koordinasi, leasing dari pembayaran di Arus Kas.</p>
      </section>
    </>
  );
}

// One truck's account for the month: the summary, then every line behind it.
function TruckSheet({ no, mk, onClose }) {
  const { api } = useV2();
  const [a, setA] = useState(null);
  useEffect(() => { let live = true; const c = new AbortController(); api.truckAccount(mk, no, c.signal).then(r => live && setA(r)).catch(e => live && setA({ error: e.message })); return () => { live = false; c.abort(); }; }, [api, mk, no]);
  const title = `Truk ${no} · ${monthName(mk, 'long')}`;
  if (!a) return <Sheet title={title} onClose={onClose}><p className="hint">Memuat…</p></Sheet>;
  if (a.error) return <Sheet title={title} onClose={onClose}><p className="hint bad">{a.error}</p></Sheet>;
  const tot = (xs, f = () => true) => xs.filter(f).reduce((t, x) => t + x.amount, 0);
  const pend = tot(a.trips), uj = tot(a.costs, x => x.bucket === 'uj'), ko = tot(a.costs, x => x.bucket === 'koordinasi'), le = tot(a.costs, x => x.bucket === 'leasing'), rp = tot(a.repairs);
  const sisa = pend - uj - ko - rp - le;
  const routeLabel = Object.fromEntries(a.routes.map(r => [r.key, r.label.split(' (')[0]]));
  const kinds = a.repairKinds.filter(([k]) => a.repairs.some(r => r.kind === k));
  const days = a.bengkel.reduce((t, b) => t + b.days, 0);
  const Line = ({ l, r, s, strong, neg }) => <div className={`ac-l ${strong ? 'strong' : ''}`}><span>{l}{s && <small>{s}</small>}</span><span className={`num ${neg ? 'muted' : ''}`}>{r}</span></div>;
  return (
    <Sheet title={title} onClose={onClose}>
      <div className="ac-sum">
        <Line l="Pendapatan" s={`${a.trips.length} rit`} r={jt(pend)} />
        <Line l="Uang jalan" r={`− ${jt(uj)}`} neg />
        <Line l="Koordinasi" r={`− ${jt(ko)}`} neg />
        <Line l="Perbaikan" s="faktur pembelian Accurate" r={`− ${jt(rp)}`} neg />
        <Line l="Leasing" r={`− ${jt(le)}`} neg />
        <Line l="Sisa" r={`${jtS(sisa)} jt`} strong />
      </div>

      <div className="lbl"><span>Perbaikan · {jt(rp)} jt</span><span>{a.repairs.length} baris</span></div>
      {a.repairs.length ? kinds.map(([k, label]) => {
        const xs = a.repairs.filter(r => r.kind === k);
        return (
          <div key={k} className="group">
            <div className="ac-gh"><b>{label}</b><span className="num">{jt(tot(xs))}</span></div>
            {xs.map((r, i) => <div key={i} className="row"><div className="grow"><div className="t" style={{ whiteSpace: 'normal' }}>{r.what}</div><div className="m">{shortDate(r.date)} · {r.vendor} · {r.number}</div></div><span className="amt">{jt(r.amount)}</span></div>)}
          </div>
        );
      }) : <p className="hint">Tidak ada faktur pembelian untuk truk ini bulan ini.</p>}

      {a.bengkel.length > 0 && <>
        <div className="lbl"><span>Di bengkel · {dec(days, 0)} hari</span><span>app mekanik</span></div>
        <div className="group">{a.bengkel.map((b, i) => <div key={i} className="row"><div className="grow"><div className="t">{shortDate(b.from)} – {b.to ? shortDate(b.to) : 'sekarang'}</div><div className="m">{b.where || 'bengkel'}</div></div><span className="amt">{dec(b.days, 0)} hr</span></div>)}</div>
      </>}

      <div className="lbl"><span>Perjalanan · {a.trips.length} rit</span><span>{jt(pend)} jt</span></div>
      <div className="group">{a.trips.map((t, i) => <div key={i} className="row"><div className="grow"><div className="t">{routeLabel[t.route] || t.customer}</div><div className="m">{shortDate(t.date)} · {t.number}</div></div><span className="amt">{jt(t.amount)}</span></div>)}</div>

      <div className="lbl"><span>UJ, koordinasi, leasing · {jt(uj + ko + le)} jt</span><span>dibayar di Arus Kas</span></div>
      <div className="group">{a.costs.map((c, i) => <div key={i} className="row"><div className="grow"><div className="t" style={{ whiteSpace: 'normal' }}>{c.what}</div><div className="m">{shortDate(c.date)}{c.shared ? ` · dibagi ${c.shared} truk` : ''}</div></div><span className="amt">{jt(c.amount)}</span></div>)}</div>
    </Sheet>
  );
}

function PerRute({ d, mk, prevK }) {
  const { isPhone } = useV2();
  const rows = d.routes.map(r => ({ ...r, m: r.months[mk] || {}, p: prevK ? r.months[prevK] || null : null })).filter(r => r.m.rit || r.m.pendapatan);
  const per = (m, k) => (m && m.rit ? (m[k] || 0) / m.rit : null);
  const cols = isPhone ? 'minmax(0,1fr) 46px 70px 70px' : 'minmax(220px,1fr) 70px 104px 96px 96px 104px 84px';
  const H = ({ children, r }) => <span style={{ textAlign: r ? 'right' : 'left' }}>{children}</span>;
  const tot = rows.reduce((t, r) => ({ rit: t.rit + (r.m.rit || 0), pendapatan: t.pendapatan + (r.m.pendapatan || 0), uj: t.uj + (r.m.uj || 0) }), { rit: 0, pendapatan: 0, uj: 0 });
  return (
    <section className="sec">
      <div className="sec-h"><div><h3>Per rute</h3><p>siapa membayar perjalanan mana · per rit</p></div><span>juta</span></div>
      <div className="tbl">
        <div className="tr h" style={{ gridTemplateColumns: cols }}>
          <H>Rute</H><H r>Rit</H>{!isPhone && <H r>Pendapatan</H>}<H r>per rit</H><H r>UJ/rit</H>{!isPhone && <><H r>Sisa per rit</H><H r>UJ / pend.</H></>}
        </div>
        {rows.map(r => {
          const pr = per(r.m, 'pendapatan'), uj = per(r.m, 'uj');
          return (
            <div key={r.key} className="tr" style={{ gridTemplateColumns: cols }}>
              <span className="k" style={{ whiteSpace: 'normal' }}>{r.label}</span>
              <span className="n">{r.m.rit}<Delta cur={r.m.rit || 0} prev={r.p ? r.p.rit || 0 : null} count /></span>
              {!isPhone && <span className="n">{jt(r.m.pendapatan || 0)}</span>}
              <span className="n">{dec(pr / 1e6)}<Delta cur={pr || 0} prev={per(r.p, 'pendapatan')} /></span>
              <span className="n muted">{dec(uj / 1e6)}</span>
              {!isPhone && <>
                <span className="n" style={{ fontWeight: 600 }}>{dec((pr - uj) / 1e6)}</span>
                <span className="n muted">{r.m.pendapatan ? `${dec((r.m.uj || 0) / r.m.pendapatan * 100, 0)}%` : '—'}</span>
              </>}
            </div>
          );
        })}
        <div className="tr day" style={{ gridTemplateColumns: cols }}>
          <span className="k">Semua rute</span><span className="n">{tot.rit}</span>{!isPhone && <span className="n">{jt(tot.pendapatan)}</span>}
          <span className="n">{tot.rit ? dec(tot.pendapatan / tot.rit / 1e6) : '—'}</span><span className="n">{tot.rit ? dec(tot.uj / tot.rit / 1e6) : '—'}</span>
          {!isPhone && <><span className="n">{tot.rit ? dec((tot.pendapatan - tot.uj) / tot.rit / 1e6) : '—'}</span><span className="n muted">{tot.pendapatan ? `${dec(tot.uj / tot.pendapatan * 100, 0)}%` : '—'}</span></>}
        </div>
      </div>
      <p className="hint">Rit dan pendapatan dari faktur Accurate. UJ dari pembayaran UJ di Arus Kas untuk customer itu. Satu perjalanan penuh biasanya pergi Indahkiat + satu balikan: sisa per rit keduanya dijumlah.</p>
    </section>
  );
}
